import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink } from "lucide-react";

// MICRO-PROMPT 2 — Public-facing intro page for The Durban 49 Group.
// Route: /d49 (parent-company introduction only).
// Strict scope:
//   - No login, no client portal, no Kandake exposure.
//   - One primary CTA -> /gfmp (future Good Friar Music Publishing page).
//   - Uses the official Durban 49 Group logo as-is (no recolour / distort / crop).
//   - Cream / ivory canvas with burgundy + gold + charcoal accents only.
export default function D49() {
  const navigate = useNavigate();
  return (
    <div
      data-testid="d49-page"
      className="min-h-screen w-full flex flex-col"
      style={{
        // Warm ivory canvas with a barely-there radial accent so the page reads
        // editorial without losing the cream-on-cream calm the brief asks for.
        background:
          "radial-gradient(1100px 600px at 50% -10%, hsl(40 55% 96%), transparent 65%)," +
          "radial-gradient(900px 500px at 100% 100%, hsl(347 30% 92% / 0.45), transparent 60%)," +
          "hsl(40 45% 96%)",
        color: "hsl(20 14% 14%)",
      }}
    >
      {/* Top rule — restrained editorial header */}
      <header className="max-w-6xl w-full mx-auto px-6 sm:px-10 pt-6 sm:pt-8 flex items-center justify-between">
        <div
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]"
          style={{ color: "hsl(347 50% 22%)" }}
          data-testid="d49-eyebrow"
        >
          <span
            aria-hidden="true"
            style={{ width: 18, height: 1, background: "hsl(347 50% 22% / 0.55)" }}
          />
          The Durban 49 Group
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.22em]"
          style={{ color: "hsl(20 14% 14% / 0.55)" }}
        >
          Parent Company
        </div>
      </header>

      {/* Hero — logo as the dominant mark, copy as restrained caption */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 sm:px-10 pt-8 sm:pt-12 pb-16 sm:pb-24 flex flex-col items-center text-center">
        {/* Logo is the hero — pushed considerably larger so the mark
            unmistakably leads the composition. Transparent PNG sits
            cleanly on the ivory canvas. Proportions preserved via
            h-auto, never recoloured or cropped. */}
        <img
          src="/brand/d49.png"
          alt="The Durban 49 Group"
          draggable="false"
          data-testid="d49-logo"
          className="block w-[320px] sm:w-[480px] md:w-[600px] lg:w-[680px] h-auto select-none mb-8 sm:mb-10"
        />

        {/* Headline pulled back so it supports the logo without
            competing. Still elegant, but no longer the focal weight. */}
        <h1
          className="font-display text-[26px] sm:text-[34px] md:text-[40px] leading-[1.1] tracking-tight max-w-2xl font-medium"
          data-testid="d49-headline"
          style={{ color: "hsl(20 14% 14% / 0.86)" }}
        >
          Music. Rights.{" "}
          <span style={{ color: "hsl(347 60% 22%)" }}>Creative Enterprise.</span>
        </h1>

        <p
          className="mt-5 sm:mt-6 text-[13.5px] sm:text-[15px] leading-relaxed max-w-xl"
          style={{ color: "hsl(20 14% 14% / 0.68)" }}
          data-testid="d49-body"
        >
          The Durban 49 Group is the parent company behind an integrated music,
          publishing, rights-management, and creative ecosystem.
        </p>

        <p
          className="mt-2.5 text-[12.5px] sm:text-[13.5px] max-w-xl"
          style={{ color: "hsl(20 14% 14% / 0.56)" }}
          data-testid="d49-secondary"
        >
          Discover the work of <span className="font-medium" style={{ color: "hsl(347 60% 22%)" }}>Good Friar Music Publishing</span>.
        </p>

        <div className="mt-10 sm:mt-12">
          <Button
            size="lg"
            onClick={() => navigate("/gfmp")}
            data-testid="d49-enter-gfmp-button"
            className="font-medium px-7 py-6 text-[15px] tracking-wide"
            style={{
              background: "hsl(347 60% 22%)",
              color: "hsl(40 55% 96%)",
              boxShadow: "0 6px 18px -8px hsl(347 60% 18% / 0.45)",
            }}
          >
            Enter Good Friar Music Publishing
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Group portals — sibling brands within The Durban 49 Group.
            Kandake links to the existing internal /kandake public welcome page.
            Kingswork links to its own configured public destination and opens
            in a new tab (external). No new pages, forms, analytics, or
            duplication introduced here. */}
        <div
          className="mt-14 sm:mt-20 w-full max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 text-left"
          data-testid="d49-portals"
          aria-label="Also within The Durban 49 Group"
        >
          <a
            href="https://d49kingswork.com/"
            target="_blank"
            rel="noopener"
            data-testid="d49-portal-kingswork"
            aria-label="Kingswork Music — creative music, production, releases, and catalogue (opens in a new tab)"
            className="group block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(347_60%_22%)]"
          >
            <div className="h-full rounded-lg border p-5 sm:p-6 bg-[hsl(40_45%_97%)] border-[hsl(40_30%_84%)] hover:border-[hsl(347_60%_22%_/_0.55)] hover:shadow-[0_10px_25px_-15px_hsl(347_60%_18%_/_0.35)] transition-[border-color,box-shadow,transform] duration-200 group-hover:-translate-y-0.5 group-focus-visible:-translate-y-0.5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(40_45%_38%)]">
                <span aria-hidden="true" className="inline-block w-3 h-px bg-[hsl(40_45%_55%)]" />
                Group portal · external
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <h2 className="font-display text-[18px] sm:text-[22px] font-medium text-[hsl(347_60%_22%)]">
                  Kingswork Music
                </h2>
                <ExternalLink
                  aria-hidden="true"
                  className="w-4 h-4 shrink-0 text-[hsl(20_14%_14%_/_0.55)] group-hover:text-[hsl(347_60%_22%)] group-focus-visible:text-[hsl(347_60%_22%)] transition-colors duration-200"
                />
              </div>
              <p className="mt-2 text-[13px] sm:text-[13.5px] leading-relaxed text-[hsl(20_14%_14%_/_0.7)]">
                Creative music, production, releases, and catalogue.
              </p>
            </div>
          </a>

          <Link
            to="/kandake"
            data-testid="d49-portal-kandake"
            aria-label="Kandake Compliance Service — music-rights compliance, intake, clearance, and readiness"
            className="group block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(347_60%_22%)]"
          >
            <div className="h-full rounded-lg border p-5 sm:p-6 bg-[hsl(40_45%_97%)] border-[hsl(40_30%_84%)] hover:border-[hsl(347_60%_22%_/_0.55)] hover:shadow-[0_10px_25px_-15px_hsl(347_60%_18%_/_0.35)] transition-[border-color,box-shadow,transform] duration-200 group-hover:-translate-y-0.5 group-focus-visible:-translate-y-0.5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(40_45%_38%)]">
                <span aria-hidden="true" className="inline-block w-3 h-px bg-[hsl(40_45%_55%)]" />
                Group portal · internal
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <h2 className="font-display text-[18px] sm:text-[22px] font-medium text-[hsl(347_60%_22%)]">
                  Kandake Compliance Service
                </h2>
                <ArrowRight
                  aria-hidden="true"
                  className="w-4 h-4 shrink-0 text-[hsl(20_14%_14%_/_0.55)] group-hover:text-[hsl(347_60%_22%)] group-focus-visible:text-[hsl(347_60%_22%)] transition-transform duration-200 group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5"
                />
              </div>
              <p className="mt-2 text-[13px] sm:text-[13.5px] leading-relaxed text-[hsl(20_14%_14%_/_0.7)]">
                Music-rights compliance, intake, clearance, and readiness.
              </p>
            </div>
          </Link>
        </div>

        {/* Hairline divider, then a single understated accent line */}
        <div
          className="mt-16 sm:mt-24 w-16 h-px"
          aria-hidden="true"
          style={{ background: "hsl(40 45% 55% / 0.55)" }}
        />
      </main>

      {/* Footer — minimal, copyright + brand line. No nav. */}
      <footer
        className="w-full border-t"
        style={{ borderColor: "hsl(40 30% 86%)" }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] tracking-wide"
             style={{ color: "hsl(20 14% 14% / 0.55)" }}>
          <span data-testid="d49-footer-rights">
            © {new Date().getFullYear()} The Durban 49 Group. All rights reserved.
          </span>
          <span className="uppercase tracking-[0.22em]">
            Music · Rights · Creative Enterprise
          </span>
        </div>
      </footer>
    </div>
  );
}
